import { FC } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Events } from '~/constants/events';
import { trackEvent } from '~/helpers/analytics';
import { useExtensionConfig } from '~/hooks/useExtensionConfig';

import { ExtensionLinkSendForm } from './ExtensionLinkSendForm';
import Link from './Link';

const { Button } = ToucanComponents;

interface AddExtensionFooterProps {
  className?: string;
  contentMaxWidth?: {
    sm: number;
    md: number;
    lg: number;
  };
  pageName: string;
}

export const AddExtensionFooter: FC<AddExtensionFooterProps> = ({
  children,
  contentMaxWidth = {
    sm: 372,
    md: 566,
    lg: 566,
  },
  className,
  pageName,
}) => {
  const theme = useTheme();
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const { storeUrl, browserName } = useExtensionConfig();

  return (
    <Box
      display="flex"
      flexDirection="column"
      py={3}
      px={2}
      className={className}
      css={css`
        background-color: ${ToucanColors.teal[500]};
        ${theme.breakpoints.up('sm')} {
          overflow: hidden;
          background: url(/images/add-extension-footer-bg-left.svg) left bottom no-repeat,
            url(/images/add-extension-footer-bg-right.svg) right bottom no-repeat, ${ToucanColors.teal[500]};
          flex-direction: row;
          justify-content: center;
          padding: 0;
          padding-top: ${theme.spacing(2)};
        }
      `}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        css={css`
          ${theme.breakpoints.up('sm')} {
            margin-bottom: ${theme.spacing(2)};
          }
          ${theme.breakpoints.up('md')} {
            flex-direction: row;
            align-items: center;
            margin-bottom: ${theme.spacing(2)};
          }
        `}
      >
        <div
          css={css`
            ${theme.breakpoints.up('sm')} {
              max-width: ${contentMaxWidth.sm}px;
              margin-right: ${theme.spacing(5)};
            }
            ${theme.breakpoints.up('md')} {
              max-width: ${contentMaxWidth.md}px;
            }
            ${theme.breakpoints.up('lg')} {
              max-width: ${contentMaxWidth.lg}px;
            }
          `}
        >
          {children}
        </div>
        {isViewportAboveSm ? (
          <Button
            type="secondary"
            size="medium"
            isReversed
            component={Link}
            href={storeUrl}
            onClick={() => {
              trackEvent({
                name: Events.ExtensionDownloadCtaClicked,
                properties: {
                  pageName,
                },
              });
            }}
            css={css`
              margin-top: ${theme.spacing(2)};
              ${theme.breakpoints.up('md')} {
                margin-top: 0;
              }
            `}
          >
            Add to {browserName} for free
          </Button>
        ) : (
          <ExtensionLinkSendForm
            pageName={pageName}
            buttonProps={{
              type: 'secondary',
              isReversed: true,
            }}
          />
        )}
      </Box>
      {isViewportAboveSm && (
        <img
          alt="Toucan"
          src="/images/footer-toucan.svg"
          height="149"
          css={css`
            align-self: flex-end;
            ${theme.breakpoints.up('md')} {
              max-width: ${contentMaxWidth.md}px;
              height: 88px;
              margin-left: ${theme.spacing(10)};
              margin-top: -${theme.spacing(1)};
              margin-bottom: -${theme.spacing(1)};
            }
          `}
        />
      )}
    </Box>
  );
};
